import { useEffect, useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Copy } from 'lucide-react'
import { useEditorStore } from '@/store/editorStore'
import type { Block } from '@/domain/model/Block'
import { BlockPreview } from './BlockPreview'

export const EMPTY_CANVAS_DROP_ID = 'canvas-empty-drop-zone'

function BlockContextMenu({ x, y, onDuplicate, onDelete, onClose }: {
  x: number; y: number; onDuplicate: () => void; onDelete: () => void; onClose: () => void
}) {
  useEffect(() => {
    const close = () => onClose()
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('click', close)
    window.addEventListener('keydown', onEscape)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('keydown', onEscape)
    }
  }, [onClose])

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stopPropagation only, keeps clicks on the menu buttons below from bubbling to the window-level close listener; Escape (registered above) is the keyboard equivalent
    <div
      style={{ position: 'fixed', left: x, top: y }}
      className="z-50 min-w-[140px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => { onDuplicate(); onClose() }}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
      >
        <Copy className="h-3.5 w-3.5" /> Duplicate
      </button>
      <button
        onClick={() => { onDelete(); onClose() }}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 cursor-pointer"
      >
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </button>
    </div>
  )
}

function SortableBlock({ block }: { block: Block }) {
  const isSelected = useEditorStore(s => s.selectedBlockId === block.id)
  const selectBlock = useEditorStore(s => s.selectBlock)
  const deleteBlock = useEditorStore(s => s.deleteBlock)
  const addBlock = useEditorStore(s => s.addBlock)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={`Select ${block.type} block`}
      aria-pressed={isSelected}
      onClick={() => selectBlock(block.id)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectBlock(block.id) }
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        selectBlock(block.id)
        setMenuPos({ x: e.clientX, y: e.clientY })
      }}
      className={[
        'group relative rounded-lg border-2 transition-all',
        isSelected
          ? 'border-violet-500 shadow-[0_0_0_3px_rgba(124,58,237,0.12)]'
          : 'border-transparent hover:border-gray-300',
      ].join(' ')}
    >
      {/* Block actions */}
      <div
        className={[
          'absolute -top-3 right-2 flex items-center gap-1 rounded-md bg-white border border-gray-200 shadow-sm px-1 py-0.5 z-10 transition-opacity',
          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        ].join(' ')}
      >
        <button
          {...listeners}
          {...attributes}
          className="cursor-grab p-1 text-gray-400 hover:text-gray-600"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <span className="text-[10px] font-medium text-gray-400 px-1">{block.type}</span>
        <button
          onClick={(e) => {
            e.stopPropagation()
            addBlock(block.type, block.props as never, block.id)
          }}
          className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
          title="Duplicate"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            deleteBlock(block.id)
          }}
          className="p-1 text-gray-400 hover:text-red-500 cursor-pointer"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <BlockPreview block={block} editable />

      {menuPos && (
        <BlockContextMenu
          x={menuPos.x}
          y={menuPos.y}
          onDuplicate={() => addBlock(block.type, block.props as never, block.id)}
          onDelete={() => deleteBlock(block.id)}
          onClose={() => setMenuPos(null)}
        />
      )}
    </div>
  )
}

function DropZone() {
  const { setNodeRef, isOver } = useDroppable({ id: EMPTY_CANVAS_DROP_ID })
  return (
    <div
      ref={setNodeRef}
      className={[
        'flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-16 text-center transition-colors',
        isOver ? 'border-violet-400 bg-violet-50' : 'border-gray-300',
      ].join(' ')}
    >
      <p className="text-sm font-medium text-gray-400">Drag a block here to start building</p>
      <p className="mt-1 text-xs text-gray-300">Or pick from the palette on the left</p>
    </div>
  )
}

const DEVICE_WIDTH: Record<string, string> = {
  desktop: 'w-full',
  tablet:  'w-[768px] mx-auto shadow-2xl',
  mobile:  'w-[375px] mx-auto shadow-2xl',
}

export function Canvas() {
  const blocks = useEditorStore(s => s.blocks)
  const previewMode = useEditorStore(s => s.previewMode)
  const previewDevice = useEditorStore(s => s.previewDevice)

  return (
    <div className={['flex-1 overflow-y-auto', previewMode ? 'bg-gray-200 py-6' : 'bg-gray-100 p-6'].join(' ')}>
      <div className={previewMode ? `bg-white overflow-hidden ${DEVICE_WIDTH[previewDevice] ?? 'w-full'}` : 'mx-auto max-w-4xl'}>
        {blocks.length === 0 ? (
          <DropZone />
        ) : (
          <SortableContext
            items={blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={previewMode ? '' : 'space-y-2'}>
              {blocks.map((block) =>
                previewMode ? (
                  <BlockPreview key={block.id} block={block} />
                ) : (
                  <SortableBlock key={block.id} block={block} />
                ),
              )}
            </div>
          </SortableContext>
        )}
      </div>
    </div>
  )
}
